<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import SelectSearch from '@/Components/SelectSearch.vue';

const props = defineProps({
    courses: Array,
    students: Array,
    bankAccounts: Array,
    anioEscolar: Number,
    permissions: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['payment-saved']);

// Opciones de estudiantes para SelectSearch
const studentOptions = computed(() => {
    return (props.students || []).map(s => ({
        value: s.id,
        label: `${s.apellidos} ${s.nombres} ${s.num_doc ? '(' + s.num_doc + ')' : ''}`
    }));
});

// Estado de la tabla de pagos
const payments = ref([]);
const pagination = ref({ current_page: 1, last_page: 1, total: 0 });
const loading = ref(false);

// Filtros
const filters = ref({
    id_bank_account: '',
    id_student: '',
    payment_method: '',
    search: '',
    page: 1,
    per_page: 15
});

const fetchPayments = async (page = 1) => {
    if (props.permissions?.payments_read === false) return;
    loading.value = true;
    filters.value.page = page;
    try {
        const res = await axios.get(route('financiera-payments.list'), {
            params: filters.value
        });
        payments.value = res.data.data;
        pagination.value = {
            current_page: res.data.current_page,
            last_page: res.data.last_page,
            total: res.data.total
        };
    } catch (err) {
        if (err.response?.status !== 403) {
            console.error('Error al cargar pagos:', err);
        }
    } finally {
        loading.value = false;
    }
};

let searchTimeout = null;
const onSearchInput = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchPayments(1);
    }, 400);
};

// Modal de Nuevo Pago
const showPaymentModal = ref(false);
const savingPayment = ref(false);
const paymentError = ref('');
const paymentForm = ref({
    amount: 150.00,
    id_bank_account: '',
    id_student: '',
    fecha_pago: new Date().toISOString().substring(0, 10),
    operation_number: '',
    payment_method: 'transferencia',
    note: '',
    allocations: [] // [{ id_order, amount, label, total_amount, balance }]
});
const fileInput = ref(null);
const attachedFile = ref(null);

// Órdenes pendientes del estudiante seleccionado
const studentPendingOrders = ref([]);
const loadingStudentOrders = ref(false);

const loadStudentPendingOrders = async (studentId) => {
    if (!studentId) {
        studentPendingOrders.value = [];
        paymentForm.value.allocations = [];
        return;
    }
    loadingStudentOrders.value = true;
    try {
        const res = await axios.get(route('financiera-students.pending-orders', studentId));
        studentPendingOrders.value = res.data;

        // Auto-distribuir si el monto del pago cubre las órdenes
        distributePaymentAmount();
    } catch (err) {
        console.error('Error al cargar órdenes del alumno:', err);
    } finally {
        loadingStudentOrders.value = false;
    }
};

const distributePaymentAmount = () => {
    let remaining = Number(paymentForm.value.amount) || 0;
    const newAllocations = [];

    for (const order of studentPendingOrders.value) {
        const orderBalance = Number(order.balance);
        if (remaining <= 0) break;

        const allocAmount = Math.min(remaining, orderBalance);
        newAllocations.push({
            id_order: order.id,
            amount: allocAmount,
            ref: order.ref,
            label: order.label,
            course_name: order.course?.label || 'General',
            balance: orderBalance,
            selected: true
        });
        remaining -= allocAmount;
    }

    paymentForm.value.allocations = newAllocations;
};

const onFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
        attachedFile.value = files[0];
    } else {
        attachedFile.value = null;
    }
};

const openCreatePaymentModal = (prefill = null) => {
    paymentError.value = '';
    attachedFile.value = null;
    if (fileInput.value) fileInput.value.value = '';

    paymentForm.value = {
        amount: prefill?.preselected_order ? Number(prefill.preselected_order.balance) : 150.00,
        id_bank_account: props.bankAccounts.length > 0 ? props.bankAccounts[0].id : '',
        id_student: prefill?.id_student || '',
        fecha_pago: new Date().toISOString().substring(0, 10),
        operation_number: '',
        payment_method: 'transferencia',
        note: '',
        allocations: []
    };

    if (paymentForm.value.id_student) {
        loadStudentPendingOrders(paymentForm.value.id_student);
    }

    showPaymentModal.value = true;
};

const totalAllocated = () => {
    return paymentForm.value.allocations
        .filter(a => a.selected)
        .reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
};

const submitPayment = async () => {
    savingPayment.value = true;
    paymentError.value = '';

    if (!paymentForm.value.id_student) {
        paymentError.value = 'Debe seleccionar un estudiante para registrar y aplicar el pago a sus órdenes.';
        savingPayment.value = false;
        return;
    }

    const formData = new FormData();
    formData.append('amount', paymentForm.value.amount);
    formData.append('id_bank_account', paymentForm.value.id_bank_account);
    formData.append('id_student', paymentForm.value.id_student);
    formData.append('fecha_pago', paymentForm.value.fecha_pago);
    formData.append('operation_number', paymentForm.value.operation_number || '');
    formData.append('payment_method', paymentForm.value.payment_method);
    formData.append('note', paymentForm.value.note || '');

    const selectedAllocations = paymentForm.value.allocations.filter(a => a.selected && Number(a.amount) > 0);
    selectedAllocations.forEach((alloc, index) => {
        formData.append(`allocations[${index}][id_order]`, alloc.id_order);
        formData.append(`allocations[${index}][amount]`, alloc.amount);
    });

    if (attachedFile.value) {
        formData.append('file', attachedFile.value);
    }

    try {
        await axios.post(route('financiera-payments.store'), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        showPaymentModal.value = false;
        fetchPayments(1);
        emit('payment-saved');
    } catch (err) {
        paymentError.value = err.response?.data?.message || 'Error al registrar el pago.';
    } finally {
        savingPayment.value = false;
    }
};

// Modal de Documentos de Sustento
const showDocumentsModal = ref(false);
const activePaymentForDocs = ref(null);
const documentsList = ref([]);
const loadingDocs = ref(false);
const uploadingDoc = ref(false);
const newDocFile = ref(null);
const newDocLabel = ref('');

const openDocumentsModal = async (payment) => {
    activePaymentForDocs.value = payment;
    newDocFile.value = null;
    newDocLabel.value = '';
    showDocumentsModal.value = true;
    loadPaymentDocuments(payment.id);
};

const loadPaymentDocuments = async (paymentId) => {
    loadingDocs.value = true;
    try {
        const res = await axios.get(route('financiera-payments.documents', paymentId));
        documentsList.value = res.data;
    } catch (err) {
        console.error('Error al cargar documentos:', err);
    } finally {
        loadingDocs.value = false;
    }
};

const onNewDocFileChange = (e) => {
    if (e.target.files.length > 0) {
        newDocFile.value = e.target.files[0];
    }
};

const uploadNewDocument = async () => {
    if (!newDocFile.value || !activePaymentForDocs.value) return;
    uploadingDoc.value = true;
    const formData = new FormData();
    formData.append('file', newDocFile.value);
    formData.append('label', newDocLabel.value);

    try {
        await axios.post(route('financiera-payments.upload-document', activePaymentForDocs.value.id), formData);
        newDocFile.value = null;
        newDocLabel.value = '';
        loadPaymentDocuments(activePaymentForDocs.value.id);
        fetchPayments(pagination.value.current_page);
    } catch (err) {
        alert(err.response?.data?.message || 'Error al subir documento.');
    } finally {
        uploadingDoc.value = false;
    }
};

const deleteDocument = async (docId) => {
    if (!confirm('¿Desea eliminar este documento de sustento?')) return;
    try {
        await axios.delete(route('financiera-documents.destroy', docId));
        if (activePaymentForDocs.value) {
            loadPaymentDocuments(activePaymentForDocs.value.id);
        }
        fetchPayments(pagination.value.current_page);
    } catch (err) {
        alert(err.response?.data?.message || 'Error al eliminar documento.');
    }
};

// Eliminar Pago
const deletePayment = async (payment) => {
    if (!confirm(`¿Está seguro de eliminar el pago ${payment.ref} de S/ ${payment.amount}? Los saldos de las órdenes aplicadas se revertirán automáticamente.`)) return;
    try {
        const res = await axios.delete(route('financiera-payments.destroy', payment.id));
        if (res.data.success) {
            fetchPayments(pagination.value.current_page);
            emit('payment-saved');
        } else {
            alert(res.data.message);
        }
    } catch (err) {
        alert(err.response?.data?.message || 'Error al eliminar el pago.');
    }
};

defineExpose({
    openCreatePaymentModal
});

onMounted(() => {
    fetchPayments(1);
});
</script>

<template>
    <div class="space-y-6">
        <!-- Barra de Filtros y Acciones -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 shadow-sm space-y-4">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <button v-if="permissions?.payments_write !== false" @click="openCreatePaymentModal()" class="btn-premium-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md transition-all">
                    <Icon icon="tabler:cash-plus" class="w-5 h-5" />
                    <span>Registrar Nuevo Pago</span>
                </button>
                <div v-else></div>

                <div class="flex items-center gap-3">
                    <span class="text-xs font-semibold text-slate-400">Total: {{ pagination.total }} pagos registrados</span>
                </div>
            </div>

            <!-- Filtros secundarios -->
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                <!-- Buscador -->
                <div class="relative">
                    <Icon icon="tabler:search" class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input type="text" v-model="filters.search" @input="onSearchInput" placeholder="Buscar ref, operación, alumno..." class="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                </div>

                <!-- Cuenta Bancaria -->
                <div>
                    <select v-model="filters.id_bank_account" @change="fetchPayments(1)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                        <option value="">Todas las cuentas</option>
                        <option v-for="ba in bankAccounts" :key="ba.id" :value="ba.id">{{ ba.bank_name }} - {{ ba.label }} ({{ ba.account_number }})</option>
                    </select>
                </div>

                <!-- Método de Pago -->
                <div>
                    <select v-model="filters.payment_method" @change="fetchPayments(1)" class="w-full py-2 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                        <option value="">Todos los métodos</option>
                        <option value="transferencia">Transferencia Bancaria</option>
                        <option value="deposito">Depósito en Ventanilla</option>
                        <option value="efectivo">Caja / Efectivo</option>
                        <option value="yape">Yape / Plin</option>
                        <option value="pos">POS / Tarjeta</option>
                    </select>
                </div>

                <!-- Botón Reset -->
                <div>
                    <button @click="filters = { id_bank_account: '', id_student: '', payment_method: '', search: '', page: 1, per_page: 15 }; fetchPayments(1);" class="w-full py-2 px-3 text-xs font-semibold rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-1.5">
                        <Icon icon="tabler:refresh" class="w-4 h-4" />
                        <span>Limpiar</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Tabla de Pagos -->
        <div class="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
            <div v-if="loading" class="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
                <Icon icon="tabler:loader-2" class="w-8 h-8 animate-spin text-emerald-500" />
                <span class="text-sm font-medium">Cargando pagos...</span>
            </div>

            <div v-else-if="payments.length === 0" class="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-3">
                <Icon icon="tabler:credit-card-off" class="w-12 h-12 text-slate-300 dark:text-slate-600" />
                <p class="text-base font-semibold text-slate-600 dark:text-slate-300">No se encontraron pagos registrados</p>
                <p class="text-xs text-slate-400 max-w-sm">Haga clic en "Registrar Nuevo Pago" para registrar un abono y distribuirlo entre las órdenes pendientes.</p>
            </div>

            <div v-else class="overflow-x-auto">
                <table class="w-full text-left text-xs">
                    <thead class="bg-slate-50 dark:bg-slate-900/60 text-slate-500 font-semibold uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                        <tr>
                            <th class="py-3.5 px-4">Ref / Fecha</th>
                            <th class="py-3.5 px-4">Estudiante / Alumno</th>
                            <th class="py-3.5 px-4">Cuenta / Operación</th>
                            <th class="py-3.5 px-4">Órdenes Cubiertas</th>
                            <th class="py-3.5 px-4 text-right">Monto Total</th>
                            <th class="py-3.5 px-4 text-right">Aplicado</th>
                            <th class="py-3.5 px-4 text-center">Sustentos</th>
                            <th class="py-3.5 px-4 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 dark:divide-slate-700/60 font-medium">
                        <tr v-for="pay in payments" :key="pay.id" class="hover:bg-slate-50/60 dark:hover:bg-slate-750 transition-colors">
                            <!-- Ref y Fecha -->
                            <td class="py-3 px-4">
                                <div class="font-bold text-slate-800 dark:text-white">{{ pay.ref }}</div>
                                <div class="text-[11px] text-slate-400 flex items-center gap-1">
                                    <Icon icon="tabler:calendar" class="w-3.5 h-3.5" />
                                    {{ pay.fecha_pago }}
                                </div>
                            </td>

                            <!-- Estudiante -->
                            <td class="py-3 px-4">
                                <div v-if="pay.student" class="font-semibold text-slate-800 dark:text-slate-200">
                                    {{ pay.student.person?.apellidos }} {{ pay.student.person?.nombres }}
                                </div>
                                <div v-else class="text-slate-400 italic">Varios / No especificado</div>
                            </td>

                            <!-- Cuenta y Operación -->
                            <td class="py-3 px-4">
                                <div class="font-semibold text-indigo-600 dark:text-indigo-400">
                                    {{ pay.bank_account?.bank_name }} - {{ pay.bank_account?.label }}
                                </div>
                                <div class="text-[11px] text-slate-500">
                                    Op: <span class="font-bold text-slate-700 dark:text-slate-300">{{ pay.operation_number || 'S/N' }}</span> | {{ pay.payment_method }}
                                </div>
                            </td>

                            <!-- Órdenes Cubiertas -->
                            <td class="py-3 px-4">
                                <div v-if="pay.orders && pay.orders.length" class="flex flex-wrap gap-1 max-w-xs">
                                    <span v-for="o in pay.orders" :key="o.id" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold">
                                        {{ o.ref }} (S/ {{ Number(o.pivot?.amount || 0).toFixed(2) }})
                                    </span>
                                </div>
                                <div v-else class="text-slate-400 italic text-[11px]">Sin órdenes aplicadas (Saldo a favor)</div>
                            </td>

                            <!-- Monto Total -->
                            <td class="py-3 px-4 text-right font-bold text-slate-900 dark:text-white text-sm">
                                S/ {{ Number(pay.amount).toFixed(2) }}
                            </td>

                            <!-- Aplicado / Saldo -->
                            <td class="py-3 px-4 text-right">
                                <div class="font-semibold text-emerald-600 dark:text-emerald-400">S/ {{ Number(pay.applied_amount).toFixed(2) }}</div>
                                <div v-if="Number(pay.surplus_amount) > 0" class="text-[10px] text-amber-600 font-bold">
                                    Sobrante: S/ {{ Number(pay.surplus_amount).toFixed(2) }}
                                </div>
                            </td>

                            <!-- Sustentos de Pago (Documentos) -->
                            <td class="py-3 px-4 text-center">
                                <button @click="openDocumentsModal(pay)" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-semibold transition-colors text-xs">
                                    <Icon icon="tabler:paperclip" class="w-4 h-4 text-indigo-500" />
                                    <span>{{ pay.documents?.length || 0 }} archivos</span>
                                </button>
                            </td>

                            <!-- Acciones -->
                            <td class="py-3 px-4 text-center">
                                <div class="flex items-center justify-center gap-1.5">
                                    <button v-if="permissions?.payments_write !== false" @click="deletePayment(pay)" title="Eliminar Pago" class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:hover:bg-rose-900/60 dark:text-rose-400 transition-colors">
                                        <Icon icon="tabler:trash" class="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Paginación -->
            <div v-if="pagination.last_page > 1" class="px-5 py-3.5 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <span class="text-xs text-slate-500">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
                <div class="flex items-center gap-1.5">
                    <button :disabled="pagination.current_page <= 1" @click="fetchPayments(pagination.current_page - 1)" class="px-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-white dark:hover:bg-slate-800">Anterior</button>
                    <button :disabled="pagination.current_page >= pagination.last_page" @click="fetchPayments(pagination.current_page + 1)" class="px-3 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-white dark:hover:bg-slate-800">Siguiente</button>
                </div>
            </div>
        </div>

        <!-- Modal Registrar Pago -->
        <div v-if="showPaymentModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 my-8 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <div class="flex items-center gap-2.5">
                        <div class="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                            <Icon icon="tabler:cash-plus" class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">Registrar Pago</h3>
                            <p class="text-xs text-slate-400">Registra un comprobante/voucher y distribúyelo en las órdenes</p>
                        </div>
                    </div>
                    <button @click="showPaymentModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <div v-if="paymentError" class="p-3 rounded-xl text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                    {{ paymentError }}
                </div>

                <form @submit.prevent="submitPayment" class="space-y-4 text-xs">
                    <!-- Datos del Pago -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <!-- Estudiante -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Estudiante *</label>
                            <SelectSearch
                                v-model="paymentForm.id_student"
                                :options="studentOptions"
                                placeholder="Buscar y seleccionar estudiante..."
                                @change="loadStudentPendingOrders(paymentForm.id_student)"
                            />
                        </div>

                        <!-- Cuenta Receptora -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Cuenta Bancaria Receptora</label>
                            <select v-model="paymentForm.id_bank_account" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                                <option value="">Seleccione cuenta</option>
                                <option v-for="ba in bankAccounts" :key="ba.id" :value="ba.id">{{ ba.bank_name }} - {{ ba.label }} ({{ ba.account_number }})</option>
                            </select>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <!-- Monto -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Monto Pagado (S/)</label>
                            <input type="number" step="0.01" min="0.01" v-model="paymentForm.amount" @input="distributePaymentAmount" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 font-bold" />
                        </div>

                        <!-- Fecha -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Fecha de Pago</label>
                            <input type="date" v-model="paymentForm.fecha_pago" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>

                        <!-- Nro Operación -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Nro Operación / Voucher</label>
                            <input type="text" v-model="paymentForm.operation_number" placeholder="Ej. OP-948210" class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200" />
                        </div>
                    </div>

                    <!-- Método y Sustento -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Método de Pago</label>
                            <select v-model="paymentForm.payment_method" required class="w-full py-2 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200">
                                <option value="transferencia">Transferencia Bancaria</option>
                                <option value="deposito">Depósito en Ventanilla</option>
                                <option value="efectivo">Caja / Efectivo</option>
                                <option value="yape">Yape / Plin</option>
                                <option value="pos">POS / Tarjeta</option>
                            </select>
                        </div>

                        <!-- Voucher Adjunto -->
                        <div>
                            <label class="block font-semibold text-slate-700 dark:text-slate-300 mb-1">Adjuntar Voucher / Sustento</label>
                            <input type="file" ref="fileInput" @change="onFileChange" accept="image/*,.pdf" class="w-full text-xs text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                        </div>
                    </div>

                    <!-- Distribución a Órdenes Pendientes -->
                    <div class="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                        <div class="flex items-center justify-between">
                            <span class="font-bold text-slate-700 dark:text-slate-300">Aplicar a Órdenes del Estudiante:</span>
                            <span class="text-slate-500">
                                Total Asignado: <strong class="text-indigo-600 dark:text-indigo-400">S/ {{ totalAllocated().toFixed(2) }}</strong> de <strong class="text-slate-800 dark:text-white">S/ {{ Number(paymentForm.amount).toFixed(2) }}</strong>
                            </span>
                        </div>

                        <div v-if="loadingStudentOrders" class="p-4 text-center text-slate-400 flex items-center justify-center gap-2">
                            <Icon icon="tabler:loader-2" class="w-4 h-4 animate-spin text-indigo-500" />
                            <span>Cargando órdenes pendientes...</span>
                        </div>

                        <div v-else-if="!paymentForm.id_student" class="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-400 text-center">
                            Seleccione un estudiante arriba para ver y seleccionar sus órdenes pendientes de pago.
                        </div>

                        <div v-else-if="studentPendingOrders.length === 0" class="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-500 text-center">
                            El estudiante no tiene órdenes pendientes. El pago se guardará como saldo a favor.
                        </div>

                        <div v-else class="space-y-2 max-h-48 overflow-y-auto pr-1">
                            <div v-for="alloc in paymentForm.allocations" :key="alloc.id_order" class="flex items-center justify-between p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/60">
                                <div class="flex items-center gap-2">
                                    <input type="checkbox" v-model="alloc.selected" class="rounded text-indigo-600 focus:ring-indigo-500" />
                                    <div>
                                        <div class="font-bold text-slate-800 dark:text-white">{{ alloc.ref }} - {{ alloc.course_name }}</div>
                                        <div class="text-[11px] text-slate-400">{{ alloc.label }} | Saldo pendiente: S/ {{ alloc.balance.toFixed(2) }}</div>
                                    </div>
                                </div>
                                <div class="flex items-center gap-1.5">
                                    <span class="text-[11px] text-slate-500">Monto: S/</span>
                                    <input type="number" step="0.01" min="0.01" :max="alloc.balance" v-model="alloc.amount" :disabled="!alloc.selected" class="w-24 py-1 px-2 text-xs rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-right font-bold text-indigo-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Botones -->
                    <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <button type="button" @click="showPaymentModal = false" class="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">Cancelar</button>
                        <button type="submit" :disabled="savingPayment" class="btn-premium-primary px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-2 shadow-md">
                            <Icon v-if="savingPayment" icon="tabler:loader-2" class="w-4 h-4 animate-spin" />
                            <span>Confirmar Pago</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- Modal Pestaña Documentos de Sustento -->
        <div v-if="showDocumentsModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div class="bg-white dark:bg-slate-800 rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-700 space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
                    <div class="flex items-center gap-2.5">
                        <div class="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                            <Icon icon="tabler:file-invoice" class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800 dark:text-white">Documentos de Sustento</h3>
                            <p class="text-xs text-slate-400">Pago {{ activePaymentForDocs?.ref }} (S/ {{ activePaymentForDocs?.amount }})</p>
                        </div>
                    </div>
                    <button @click="showDocumentsModal = false" class="text-slate-400 hover:text-slate-600 rounded-lg p-1">
                        <Icon icon="tabler:x" class="w-5 h-5" />
                    </button>
                </div>

                <!-- Subida de Nuevo Documento -->
                <div v-if="permissions?.payments_write !== false" class="p-3.5 bg-slate-50 dark:bg-slate-900/80 rounded-xl space-y-2.5 border border-slate-200 dark:border-slate-700">
                    <span class="font-bold text-xs text-slate-700 dark:text-slate-300">Subir nuevo archivo de sustento:</span>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <input type="text" v-model="newDocLabel" placeholder="Descripción del archivo (opcional)" class="py-1.5 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200" />
                        <input type="file" @change="onNewDocFileChange" accept="image/*,.pdf,.doc,.docx" class="text-xs text-slate-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700" />
                    </div>
                    <div class="flex justify-end">
                        <button :disabled="!newDocFile || uploadingDoc" @click="uploadNewDocument" class="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-xs flex items-center gap-1.5 shadow-sm">
                            <Icon v-if="uploadingDoc" icon="tabler:loader-2" class="w-3.5 h-3.5 animate-spin" />
                            <Icon v-else icon="tabler:upload" class="w-3.5 h-3.5" />
                            <span>Subir Archivo</span>
                        </button>
                    </div>
                </div>

                <!-- Lista de Documentos -->
                <div class="space-y-2 max-h-60 overflow-y-auto">
                    <div v-if="loadingDocs" class="p-6 text-center text-slate-400 flex items-center justify-center gap-2">
                        <Icon icon="tabler:loader-2" class="w-4 h-4 animate-spin text-indigo-500" />
                        <span class="text-xs">Cargando documentos...</span>
                    </div>

                    <div v-else-if="documentsList.length === 0" class="p-6 text-center text-slate-400 text-xs">
                        No hay documentos adjuntos para este pago.
                    </div>

                    <div v-else v-for="doc in documentsList" :key="doc.id" class="flex items-center justify-between p-3 rounded-xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors">
                        <div class="flex items-center gap-3">
                            <div class="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-indigo-600 dark:text-indigo-400">
                                <Icon :icon="doc.extension === 'pdf' ? 'tabler:file-type-pdf' : 'tabler:photo'" class="w-5 h-5" />
                            </div>
                            <div>
                                <div class="font-bold text-xs text-slate-800 dark:text-white">{{ doc.label || doc.filename }}</div>
                                <div class="text-[10px] text-slate-400">{{ doc.extension?.toUpperCase() }} | {{ doc.created_at }}</div>
                            </div>
                        </div>

                        <div class="flex items-center gap-2">
                            <a :href="'/storage/' + doc.filepath" target="_blank" class="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300 transition-colors" title="Ver / Descargar">
                                <Icon icon="tabler:download" class="w-4 h-4" />
                            </a>
                            <button v-if="permissions?.payments_write !== false" @click="deleteDocument(doc.id)" class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 transition-colors" title="Eliminar">
                                <Icon icon="tabler:trash" class="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-700">
                    <button @click="showDocumentsModal = false" class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">Cerrar</button>
                </div>
            </div>
        </div>
    </div>
</template>
